import { createController } from '@eo/shared';

import {
  disconnectGoogle,
  disconnectSlack,
  fetchClickupStatus,
  fetchGoogleStatus,
  fetchGranolaStatus,
  fetchSlackStatus,
  removeClickupToken,
  removeGranolaKey,
  saveClickupToken,
  saveGranolaKey,
} from './api/api';
import type { GranolaStatusResponse, TokenStatusResponse } from './api/types';
import type { SettingsState } from './types';

const NO_TOKEN: TokenStatusResponse = {
  connected: false,
  preview: null,
  account: null,
  verifiedAt: null,
};

const initialState: SettingsState = {
  clickup: NO_TOKEN,
  google: { connected: false, email: null },
  granola: { ...NO_TOKEN, webhookRegistered: false, lastNote: null },
  slack: { connected: false, slackUserId: null },
};

export const settingsController = createController<SettingsState>({
  name: 'settings',
  state: initialState,
  fetchData: async () => {
    const [clickup, google, granola, slack] = await Promise.all([
      fetchClickupStatus(),
      fetchGoogleStatus(),
      fetchGranolaStatus(),
      fetchSlackStatus(),
    ]);
    return { clickup, google, granola, slack };
  },
  asyncActions: {
    saveClickup: {
      action: async (payload) => saveClickupToken((payload as { token: string }).token),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          clickup: (action as { payload: TokenStatusResponse }).payload,
        }),
      },
    },
    removeClickup: {
      action: async () => removeClickupToken(),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          clickup: (action as { payload: TokenStatusResponse }).payload,
        }),
      },
    },
    saveGranola: {
      action: async (payload) => saveGranolaKey((payload as { token: string }).token),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          granola: (action as { payload: GranolaStatusResponse }).payload,
        }),
      },
    },
    removeGranola: {
      action: async () => removeGranolaKey(),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          granola: (action as { payload: GranolaStatusResponse }).payload,
        }),
      },
    },
    disconnectGoogle: {
      action: async () => {
        await disconnectGoogle();
        return null;
      },
      handlers: {
        onSuccess: (state) => ({ ...state, google: { connected: false, email: null } }),
      },
    },
    disconnectSlack: {
      action: async () => {
        await disconnectSlack();
        return null;
      },
      handlers: {
        onSuccess: (state) => ({ ...state, slack: { connected: false, slackUserId: null } }),
      },
    },
  },
});
